import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { ArrowLeft, Plus, Trash2, Loader2, Zap, ChevronRight, FileText, CheckCircle2, XCircle, AlertTriangle, Search, X, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Persist extraction jobs so user can navigate away and come back
const EXTRACTION_JOB_KEY = 'mm_extraction_job';

function getStoredExtractionJob() {
  try {
    const stored = localStorage.getItem(EXTRACTION_JOB_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}

function storeExtractionJob(invoiceId, jobId) {
  localStorage.setItem(EXTRACTION_JOB_KEY, JSON.stringify({
    invoiceId,
    jobId,
    startedAt: Date.now()
  }));
}

function clearExtractionJob() {
  localStorage.removeItem(EXTRACTION_JOB_KEY);
}

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [si, setSi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState('');
  const extractPollRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  // Price matching
  const [priceCheck, setPriceCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [matchItemIdx, setMatchItemIdx] = useState(null);
  const [pdfZoom, setPdfZoom] = useState(1);
  const pdfContainerRef = useRef(null);

  useEffect(() => { load(); }, [id]);

  const resumeAttemptedRef = useRef(false);

  // Check for pending extraction job on mount (resumes if user navigated away)
  useEffect(() => {
    // Only attempt resume once per page load
    if (resumeAttemptedRef.current) return;
    
    const stored = getStoredExtractionJob();
    if (stored && stored.invoiceId === id && stored.jobId) {
      // Check if job is still valid (less than 5 minutes old)
      const ageMinutes = (Date.now() - stored.startedAt) / 1000 / 60;
      if (ageMinutes < 5) {
        // Wait for load() to complete first, then check if resume is needed
        const timer = setTimeout(async () => {
          resumeAttemptedRef.current = true;
          try {
            // Fetch current invoice state to check if items already exist
            const res = await api.get(`/invoice-transformer/supplier-invoices/${id}`);
            if (res.data.extracted_items?.length > 0 && res.data.extraction_status === 'SUCCESS') {
              console.log('[SupplierDetail] Items already in DB, clearing stale job');
              clearExtractionJob();
              setSi(res.data);
              setItems(res.data.extracted_items);
            } else {
              console.log('[SupplierDetail] No items in DB, resuming extraction polling');
              resumeExtractionPolling(stored.jobId);
            }
          } catch {
            clearExtractionJob();
          }
        }, 300);
        return () => clearTimeout(timer);
      } else {
        // Job is too old, clear it
        resumeAttemptedRef.current = true;
        clearExtractionJob();
      }
    }
  }, [id]);

  const resumeExtractionPolling = async (jobId) => {
    setExtracting(true);
    setExtractionProgress('Checking extraction status...');
    
    // Immediately check if job already completed (don't wait for interval)
    try {
      const statusRes = await api.get(`/invoice-transformer/extract-jobs/${jobId}/status`);
      const job = statusRes.data;
      
      if (job.status === 'completed') {
        clearExtractionJob();
        setExtracting(false);
        setExtractionProgress('');
        
        const extractedItems = job.result?.items || [];
        if (extractedItems.length > 0) {
          toast.success(`Extraction complete! ${extractedItems.length} line items found.`);
          setItems(extractedItems);
        } else {
          toast.info('Extraction finished. No items auto-detected.');
        }
        
        // Refresh other invoice data (but keep items from job result)
        setTimeout(async () => {
          try {
            const res = await api.get(`/invoice-transformer/supplier-invoices/${id}`);
            setSi(res.data);
            // Only update items if DB has more than what we got from job
            if ((res.data.extracted_items?.length || 0) > extractedItems.length) {
              setItems(res.data.extracted_items);
            }
          } catch {}
        }, 500);
        return;
      } else if (job.status === 'failed') {
        clearExtractionJob();
        setExtracting(false);
        setExtractionProgress('');
        toast.error(`Extraction failed: ${job.error || 'Unknown error'}`);
        load();
        return;
      }
      
      // Job still processing - continue polling
      setExtractionProgress('Still processing with GPT-4o Vision...');
      startPolling(jobId);
    } catch (e) {
      // Job not found or error - clear and reload
      clearExtractionJob();
      setExtracting(false);
      setExtractionProgress('');
      load();
    }
  };

  const startPolling = (jobId) => {
    const poll = setInterval(async () => {
      try {
        const statusRes = await api.get(`/invoice-transformer/extract-jobs/${jobId}/status`);
        const job = statusRes.data;

        if (job.status === 'completed') {
          clearInterval(poll);
          extractPollRef.current = null;
          clearExtractionJob();
          setExtracting(false);
          setExtractionProgress('');

          const extractedItems = job.result?.items || [];
          if (extractedItems.length > 0) {
            toast.success(`Extracted ${extractedItems.length} line items`);
            setItems(extractedItems);
          } else {
            toast.warning(job.result?.message || 'No line items auto-detected. Please add items manually.');
          }
          
          // Wait a moment for DB to commit, then refresh other invoice data (but keep items)
          setTimeout(async () => {
            try {
              const res = await api.get(`/invoice-transformer/supplier-invoices/${id}`);
              setSi(res.data);
              // Only update items if DB has more than what we got from job
              if ((res.data.extracted_items?.length || 0) > extractedItems.length) {
                setItems(res.data.extracted_items);
              }
            } catch {}
          }, 500);
        } else if (job.status === 'failed') {
          clearInterval(poll);
          extractPollRef.current = null;
          clearExtractionJob();
          setExtracting(false);
          setExtractionProgress('');
          toast.error(`Extraction failed: ${job.error || 'Unknown error'}`);
          load();
        } else {
          setExtractionProgress('Analyzing invoice pages...');
        }
      } catch {
        // Network error during poll — keep trying
      }
    }, 2000);
    extractPollRef.current = poll;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/invoice-transformer/supplier-invoices/${id}`);
      console.log('[SupplierDetail] Loaded invoice:', res.data.id, 'extracted_items:', res.data.extracted_items?.length || 0);
      setSi(res.data);
      const loadedItems = res.data.extracted_items || [];
      setItems(loadedItems);
      
      // If invoice already has items and status is SUCCESS, clear any stale extraction job
      if (loadedItems.length > 0 && res.data.extraction_status === 'SUCCESS') {
        const stored = getStoredExtractionJob();
        if (stored && stored.invoiceId === id) {
          console.log('[SupplierDetail] Clearing stale extraction job - items already loaded');
          clearExtractionJob();
        }
      }
    } catch (e) { 
      console.error('[SupplierDetail] Failed to load:', e);
      toast.error('Failed to load'); 
    }
    finally { setLoading(false); }
  };

  const handleExtract = async () => {
    setExtracting(true);
    setExtractionProgress('Submitting to AI...');
    try {
      const res = await api.post(`/invoice-transformer/supplier-invoices/${id}/extract`);

      if (res.data.method === 'manual') {
        toast.info('Document AI not configured — enter line items manually below');
        setExtracting(false);
        setExtractionProgress('');
        return;
      }

      // Async mode — poll for results
      if (res.data.status === 'ACCEPTED' && res.data.job_id) {
        const jobId = res.data.job_id;
        setExtractionProgress('Processing PDF with GPT-4o Vision...');
        
        // Store job so user can navigate away and come back
        storeExtractionJob(id, jobId);
        toast.info('Extraction started! You can navigate away — we\'ll save your progress.', { duration: 4000 });
        
        startPolling(jobId);
        return;
      }

      // Legacy sync response (fallback)
      if (res.data.items?.length > 0) {
        toast.success(`Extracted ${res.data.items.length} line items`);
        setItems(res.data.items);
      } else {
        toast.warning(res.data.message || 'No line items auto-detected. Please add items manually.');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Extraction failed'); }
    finally {
      if (!extractPollRef.current) {
        setExtracting(false);
        setExtractionProgress('');
      }
    }
  };

  // Cleanup polling on unmount (but don't clear storage - that happens when job completes)
  useEffect(() => {
    return () => { 
      if (extractPollRef.current) {
        clearInterval(extractPollRef.current);
      }
    };
  }, []);

  const handleRefilter = async () => {
    try {
      const res = await api.post(`/invoice-transformer/supplier-invoices/${id}/refilter`);
      if (res.data.status === 'refiltered') {
        toast.success(`Refiltered: ${res.data.before} items → ${res.data.after} items`);
        load();
      } else {
        toast.info(res.data.message || 'No items to refilter');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Refilter failed'); }
  };

  const saveItems = async () => {
    setSaving(true);
    try {
      await api.patch(`/invoice-transformer/supplier-invoices/${id}/items`, { items });
      toast.success('Items saved');
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addItem = () => setItems(p => [...p, { id: crypto.randomUUID(), desc: '', qty: 1, unit_cost: 0, line_cost: 0 }]);
  const removeItem = (idx) => setItems(p => p.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    setItems(p => {
      const next = [...p];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'qty' || field === 'unit_cost') {
        const qty = parseFloat(field === 'qty' ? value : next[idx].qty) || 0;
        const cost = parseFloat(field === 'unit_cost' ? value : next[idx].unit_cost) || 0;
        next[idx].line_cost = Math.round(qty * cost * 100) / 100;
      }
      return next;
    });
  };

  // Price check
  const handlePriceCheck = async () => {
    if (items.length === 0) return toast.error('No items to check');
    setChecking(true);
    try {
      const res = await api.post('/vendor-pricing/match-prices', {
        vendor_name: si?.supplier_vendor_name || '',
        items: items.map(i => ({ desc: i.desc, unit_cost: i.unit_cost })),
      });
      setPriceCheck(res.data);
      const s = res.data.summary;
      if (s.mismatched > 0) {
        toast.warning(`${s.mismatched} price mismatch${s.mismatched > 1 ? 'es' : ''} found!`);
      } else if (s.matched > 0) {
        toast.success(`All ${s.matched} prices match your catalog!`);
      } else {
        toast.info('No catalog entries found — upload vendor pricing first');
      }
    } catch { toast.error('Price check failed'); }
    finally { setChecking(false); }
  };

  // Manual search
  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get('/vendor-pricing/catalog-search', { params: { q, limit: 15 } });
      setSearchResults(res.data || []);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  };

  const openManualSearch = (idx) => {
    setMatchItemIdx(idx);
    setSearchQuery(items[idx]?.desc || '');
    setShowSearch(true);
    handleSearch(items[idx]?.desc || '');
  };

  const applyManualMatch = (catalogItem) => {
    if (matchItemIdx === null) return;
    const invoicePrice = parseFloat(items[matchItemIdx]?.unit_cost) || 0;
    const catalogPrice = catalogItem.unit_price || 0;
    const diff = Math.round((invoicePrice - catalogPrice) * 100) / 100;

    // Update priceCheck results
    if (priceCheck) {
      const newResults = [...(priceCheck.results || [])];
      if (newResults[matchItemIdx]) {
        newResults[matchItemIdx] = {
          ...newResults[matchItemIdx],
          catalog_price: catalogPrice,
          price_diff: diff,
          status: diff === 0 ? 'match' : 'mismatch',
          catalog_item: { id: catalogItem.id, description: catalogItem.description, unit_price: catalogPrice, vendor_name: catalogItem.vendor_name },
        };
        setPriceCheck({ ...priceCheck, results: newResults });
      }
    }
    setShowSearch(false);
    toast.success(`Matched: ${catalogItem.description} @ $${catalogPrice}`);
  };

  const getMatchStatus = (idx) => {
    if (!priceCheck?.results?.[idx]) return null;
    return priceCheck.results[idx];
  };

  const baseTotal = items.reduce((a, i) => a + (parseFloat(i.line_cost) || 0), 0);
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="text-[#3B82F6] animate-spin" size={28} /></div>;
  if (!si) return <div className="p-6 text-[#8B9AAB]">Not found</div>;

  return (
    <div className="p-6 space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b border-[#2D3F5E]/60">
        <button onClick={() => navigate('/ops/invoice-transformer')} className="text-[#5C6A7A] hover:text-[#3B82F6] transition-colors">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-0.5">// SHOP INVOICE</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-xl">
            {si.supplier_vendor_name || 'Unknown Vendor'}
            {si.supplier_invoice_number && <span className="text-[#5C6A7A] font-normal ml-2 text-sm">#{si.supplier_invoice_number}</span>}
          </h1>
          <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm">{si.customer_name} · {si.invoice_date}</p>
        </div>
        <button onClick={handleExtract} disabled={extracting}
          className="flex items-center gap-2 px-3 py-2 bg-[#3B82F6]/10 border border-[#3B82F6]/40 text-[#3B82F6] rounded-sm text-xs hover:bg-[#3B82F6]/20 transition-colors font-['JetBrains_Mono']"
          data-testid="ai-extract-btn">
          {extracting ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
          {extracting ? (extractionProgress || 'Extracting...') : 'AI Extract'}
        </button>
        {items.length > 0 && (
          <button onClick={handleRefilter}
            className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/40 text-amber-400 rounded-sm text-xs hover:bg-amber-500/20 transition-colors font-['JetBrains_Mono']"
            data-testid="refilter-btn">
            <Zap size={12} /> Refilter
          </button>
        )}
      </div>

      <div className="grid grid-cols-5 gap-5">
        {/* Left — PDF + Info */}
        <div className="col-span-2 space-y-4">
          <div className="card-industrial p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">Shop Invoice PDF</p>
              <div className="flex items-center gap-2">
                {pdfZoom !== 1 && (
                  <button onClick={() => setPdfZoom(1)}
                    className="text-[9px] font-['JetBrains_Mono'] text-[#5C6A7A] hover:text-white transition-colors px-2 py-1 border border-[#2D3F5E] rounded-sm"
                    data-testid="pdf-reset-zoom-btn">
                    {Math.round(pdfZoom * 100)}% — Reset
                  </button>
                )}
                <a href={`${BACKEND}${si.file_url}?token=${localStorage.getItem('mm_token')}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[#3B82F6] border border-[#3B82F6]/30 rounded-sm text-xs font-['JetBrains_Mono'] hover:bg-[#3B82F6]/10 transition-colors"
                  data-testid="pdf-fullscreen-btn">
                  <Maximize2 size={13} /> Full Screen
                </a>
              </div>
            </div>
            {si.file_url ? (
              <div
                ref={pdfContainerRef}
                className="overflow-auto rounded-sm border border-[#2D3F5E] bg-white cursor-grab active:cursor-grabbing"
                style={{ height: '560px' }}
                onWheel={(e) => {
                  if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    setPdfZoom(prev => {
                      const next = prev + (e.deltaY < 0 ? 0.15 : -0.15);
                      return Math.min(Math.max(next, 0.5), 4);
                    });
                  }
                }}
                data-testid="pdf-preview-wrapper"
              >
                <iframe
                  src={`${BACKEND}${si.file_url}?token=${localStorage.getItem('mm_token')}#navpanes=0`}
                  className="border-0 bg-white"
                  style={{
                    width: `${100 / pdfZoom}%`,
                    height: `${560 / pdfZoom}px`,
                    transform: `scale(${pdfZoom})`,
                    transformOrigin: 'top left',
                  }}
                  title="Supplier Invoice"
                />
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm">
                <FileText size={24} className="text-[#2D3F5E]" />
              </div>
            )}
            <p className="text-[8px] font-['JetBrains_Mono'] text-[#5C6A7A]/60 mt-2 text-center">Ctrl + Scroll to zoom</p>
          </div>
          <div className="card-industrial p-4 space-y-2">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-2">Invoice Info</p>
            {[
              ['Customer', si.customer_name],
              ['Vendor', si.supplier_vendor_name || '—'],
              ['Invoice #', si.supplier_invoice_number || '—'],
              ['Date', si.invoice_date || '—'],
              ['Unit', si.truck_unit_number || si.truck_id || '—'],
              ['Repair', si.repair_id ? 'Linked' : '—'],
              ['Status', si.extraction_status || '—'],
              ['Notes', si.notes || '—'],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between text-xs">
                <span className="text-[#5C6A7A] font-['IBM_Plex_Sans']">{label}</span>
                <span className={`font-['IBM_Plex_Sans'] text-right max-w-[60%] ${
                  label === 'Status' && value === 'SUCCESS' ? 'text-[#57EB31]' :
                  label === 'Status' && value === 'FAILED' ? 'text-red-400' :
                  label === 'Unit' && value !== '—' ? 'text-[#3B82F6] font-bold' : 'text-[#B8C0CC]'
                }`}>{value}</span>
              </div>
            ))}
          </div>

          {/* Price Check Summary */}
          {priceCheck && (
            <div className="card-industrial p-4" data-testid="price-check-summary">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Price Verification</p>
              {priceCheck.vendor && (
                <p className="text-xs font-['IBM_Plex_Sans'] text-[#B8C0CC] mb-3">
                  Matched against: <strong className="text-[#3B82F6]">{priceCheck.vendor.name}</strong>
                </p>
              )}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded-sm bg-[#57EB31]/10 border border-[#57EB31]/30 text-center">
                  <p className="text-lg font-['JetBrains_Mono'] font-bold text-[#57EB31]">{priceCheck.summary.matched}</p>
                  <p className="text-[9px] font-['JetBrains_Mono'] text-[#57EB31]/70">MATCH</p>
                </div>
                <div className="p-2 rounded-sm bg-red-500/10 border border-red-500/30 text-center">
                  <p className="text-lg font-['JetBrains_Mono'] font-bold text-red-400">{priceCheck.summary.mismatched}</p>
                  <p className="text-[9px] font-['JetBrains_Mono'] text-red-400/70">MISMATCH</p>
                </div>
                <div className="p-2 rounded-sm bg-[#3B82F6]/10 border border-[#3B82F6]/30 text-center">
                  <p className="text-lg font-['JetBrains_Mono'] font-bold text-[#3B82F6]">{priceCheck.summary.unmatched}</p>
                  <p className="text-[9px] font-['JetBrains_Mono'] text-[#3B82F6]/70">NEW</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right — Line Items */}
        <div className="col-span-3 space-y-4">
          {/* Credit Memo Warning — DO NOT bill customer */}
          {si.is_credit_memo && (
            <div className="rounded-sm border-2 p-4"
              style={{ background: 'rgba(239, 68, 68, 0.08)', borderColor: 'rgba(239, 68, 68, 0.55)' }}
              data-testid="credit-memo-warning">
              <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1" style={{ color: 'rgb(248, 113, 113)' }}>
                🔴 CREDIT MEMO DETECTED
              </p>
              <p className="font-['IBM_Plex_Sans'] text-sm font-bold text-white">
                {si.credit_memo_reason || 'Vendor owes you money — this is NOT a regular invoice.'}
              </p>
              <p className="font-['IBM_Plex_Sans'] text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                Do NOT bill the customer. Forward to accounting to apply the credit to the vendor account.
              </p>
            </div>
          )}
          {/* Core Charge Warning — Hide from customer invoice, collect old part */}
          {si.has_core_charges && (
            <div className="rounded-sm border-2 p-4"
              style={{ background: 'rgba(245, 158, 11, 0.08)', borderColor: 'rgba(245, 158, 11, 0.55)' }}
              data-testid="core-charge-warning">
              <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1" style={{ color: 'rgb(252, 211, 77)' }}>
                🟡 CORE CHARGE PRESENT
              </p>
              <p className="font-['IBM_Plex_Sans'] text-sm font-bold text-white">
                {si.core_charges_note || 'This invoice contains a refundable core deposit.'}
              </p>
              <p className="font-['IBM_Plex_Sans'] text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                Core deposit is hidden from customer invoice. Collect the OLD part from the truck and return it to the vendor to get your deposit refunded.
              </p>
            </div>
          )}
          {/* Total Verification Alert */}
          {si.vendor_invoice_total > 0 && (
            <div className={`rounded-sm border p-3 ${
              Math.abs((si.vendor_invoice_total || 0) - (si.parts_total || 0)) < 1
                ? 'bg-[#57EB31]/5 border-[#57EB31]/30'
                : 'bg-amber-500/5 border-amber-500/30'
            }`} data-testid="total-verification-alert">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-2">
                    {Math.abs((si.vendor_invoice_total || 0) - (si.parts_total || 0)) < 1 ? '✓ Totals Match' : '⚠ Total Mismatch — Review Items'}
                  </p>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <p className="text-[#5C6A7A] font-['JetBrains_Mono'] text-[9px]">Vendor Invoice</p>
                      <p className="font-['JetBrains_Mono'] font-bold text-white">${(si.vendor_invoice_total || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}</p>
                    </div>
                    <div>
                      <p className="text-[#5C6A7A] font-['JetBrains_Mono'] text-[9px]">Extracted Total</p>
                      <p className="font-['JetBrains_Mono'] font-bold text-[#3B82F6]">${(si.parts_total || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}</p>
                    </div>
                    <div>
                      <p className="text-[#5C6A7A] font-['JetBrains_Mono'] text-[9px]">Difference</p>
                      <p className={`font-['JetBrains_Mono'] font-bold ${
                        Math.abs((si.vendor_invoice_total || 0) - (si.parts_total || 0)) < 1 ? 'text-[#57EB31]' : 'text-amber-400'
                      }`}>
                        ${Math.abs((si.vendor_invoice_total || 0) - (si.parts_total || 0)).toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card-industrial overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#2D3F5E]">
              <div>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">Line Items</p>
                <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans']">Enter costs from the shop invoice. Customer sees marked-up prices only.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={handlePriceCheck} disabled={checking || items.length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-500/10 border border-violet-500/40 text-violet-400 rounded-sm text-xs hover:bg-violet-500/20 transition-colors font-['JetBrains_Mono'] disabled:opacity-40"
                  data-testid="price-check-btn">
                  {checking ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />}
                  {checking ? 'Checking...' : 'Price Check'}
                </button>
                <button onClick={addItem} className="flex items-center gap-1.5 px-3 py-1.5 border border-[#2D3F5E] text-[#8B9AAB] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
                  <Plus size={11} /> Add Item
                </button>
                <button onClick={saveItems} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3B82F6] text-white rounded-sm text-xs font-bold font-['Manrope'] uppercase tracking-wide hover:bg-[#2563EB] disabled:opacity-50">
                  {saving ? <Loader2 size={11} className="animate-spin" /> : null} Save
                </button>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2D3F5E]">
                  {['DESCRIPTION', 'QTY', 'UNIT COST (SHOP)', 'LINE TOTAL', 'PRICE CHECK', ''].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">
                    No items yet. Click "Add Item" or use "AI Extract" to auto-fill from the PDF.
                  </td></tr>
                ) : items.map((item, idx) => {
                  const match = getMatchStatus(idx);
                  return (
                    <tr key={item.id || idx} className={`border-b border-[#2D3F5E]/30 ${item.category === 'labor' ? 'bg-amber-500/5' : item.category === 'fee' ? 'bg-red-500/5' : ''}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {item.category && item.category !== 'part' && (
                            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[8px] font-['JetBrains_Mono'] font-bold uppercase ${
                              item.category === 'labor' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                              'bg-red-400/20 text-red-400 border border-red-400/30'
                            }`}>{item.category}</span>
                          )}
                          <input value={item.desc} onChange={e => updateItem(idx, 'desc', e.target.value)}
                            placeholder="Part description..."
                            title={item.desc}
                            className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                        </div>
                      </td>
                      <td className="px-2 py-2 w-20">
                        <input type="number" step="0.01" value={item.qty} onChange={e => updateItem(idx, 'qty', e.target.value)}
                          className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6] text-center" />
                      </td>
                      <td className="px-3 py-2 w-28">
                        <div className="flex items-center gap-1">
                          <span className="text-[#5C6A7A] text-xs">$</span>
                          <input type="number" step="0.01" value={item.unit_cost} onChange={e => updateItem(idx, 'unit_cost', e.target.value)}
                            className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                        </div>
                      </td>
                      <td className="px-3 py-2 w-24">
                        <span className="font-['JetBrains_Mono'] text-xs text-[#B8C0CC]">{fmt(item.line_cost)}</span>
                      </td>
                      <td className="px-3 py-2 w-36" data-testid={`price-check-${idx}`}>
                        {!match ? (
                          <span className="text-[9px] text-[#5C6A7A] font-['JetBrains_Mono']">—</span>
                        ) : match.status === 'match' ? (
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 size={13} className="text-[#57EB31] flex-shrink-0" />
                            <div>
                              <p className="text-[10px] font-['JetBrains_Mono'] text-[#57EB31]">Match</p>
                              <p className="text-[9px] text-[#5C6A7A] font-['JetBrains_Mono']">{fmt(match.catalog_price)}</p>
                            </div>
                          </div>
                        ) : match.status === 'mismatch' ? (
                          <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => openManualSearch(idx)} title="Click to search manually">
                            <XCircle size={13} className="text-red-400 flex-shrink-0" />
                            <div>
                              <p className="text-[10px] font-['JetBrains_Mono'] text-red-400">Mismatch</p>
                              <p className="text-[9px] font-['JetBrains_Mono']">
                                <span className="text-[#5C6A7A]">Cat: {fmt(match.catalog_price)}</span>
                                <span className={`ml-1 ${match.price_diff > 0 ? 'text-red-400' : 'text-[#57EB31]'}`}>
                                  ({match.price_diff > 0 ? '+' : ''}{fmt(match.price_diff)})
                                </span>
                              </p>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => openManualSearch(idx)}
                            className="flex items-center gap-1.5 text-[#3B82F6] hover:text-[#3B82F6]/80 transition-colors"
                            data-testid={`manual-match-${idx}`}>
                            <AlertTriangle size={13} className="flex-shrink-0" />
                            <span className="text-[10px] font-['JetBrains_Mono']">No match — Search</span>
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => removeItem(idx)} className="text-[#2D3F5E] hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  );
                })}
                {items.length > 0 && (
                  <tr className="border-t border-[#2D3F5E] bg-[#0F172A]">
                    <td colSpan={3} className="px-4 py-3 text-right text-xs font-bold font-['JetBrains_Mono'] text-[#5C6A7A] uppercase tracking-wider">Base Total (Ops Only)</td>
                    <td className="px-4 py-3 font-['JetBrains_Mono'] font-bold text-[#3B82F6]">{fmt(baseTotal)}</td>
                    <td colSpan={2} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {items.length > 0 && (
            <div className="card-industrial overflow-hidden" data-testid="customer-invoice-preview">
              <div className="px-5 py-3 border-b border-[#2D3F5E]">
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">Customer Invoice Preview</p>
                <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans']">Estimated view with default markup. Adjust in Build step.</p>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div className="flex justify-between text-xs font-['IBM_Plex_Sans']">
                  <span className="text-[#5C6A7A]">Bill To:</span>
                  <span className="text-white font-bold">{si.customer_name}</span>
                </div>
                {si.truck_unit_number && (
                  <div className="flex justify-between text-xs font-['IBM_Plex_Sans']">
                    <span className="text-[#5C6A7A]">Unit:</span>
                    <span className="text-[#3B82F6] font-bold">{si.truck_unit_number}</span>
                  </div>
                )}
                <div className="border-t border-[#2D3F5E] pt-3">
                  <table className="w-full">
                    <thead>
                      <tr>
                        {['ITEM', 'QTY', 'PRICE', 'TOTAL'].map(h => (
                          <th key={h} className="text-left text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] pb-2">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.filter(i => !i.is_labor_summary).map((item, idx) => {
                        const markedUp = Math.round((parseFloat(item.unit_cost) || 0) * 1.10 * 100) / 100;
                        const lineTotal = Math.round(markedUp * (parseFloat(item.qty) || 1) * 100) / 100;
                        return (
                          <tr key={item.id || idx} className={`border-t border-[#2D3F5E]/20 ${item.category === 'labor' ? 'bg-amber-500/5' : item.category === 'fee' ? 'bg-red-500/5' : ''}`}>
                            <td className="py-1.5 text-xs font-['IBM_Plex_Sans'] text-[#B8C0CC]">
                              {item.category && item.category !== 'part' && (
                                <span className={`mr-1 px-1 py-0.5 rounded text-[7px] font-['JetBrains_Mono'] font-bold uppercase ${
                                  item.category === 'labor' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-400/20 text-red-400'
                                }`}>{item.category}</span>
                              )}
                              {item.desc}
                            </td>
                            <td className="py-1.5 text-xs font-['JetBrains_Mono'] text-[#B8C0CC]">{item.qty}</td>
                            <td className="py-1.5 text-xs font-['JetBrains_Mono'] text-[#B8C0CC]">{fmt(markedUp)}</td>
                            <td className="py-1.5 text-xs font-['JetBrains_Mono'] text-white font-bold">{fmt(lineTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-[#2D3F5E] pt-3 space-y-1.5">
                  {(() => {
                    const partsOnly = items.filter(i => !i.is_labor_summary);
                    const subtotal = partsOnly.reduce((a, i) => a + Math.round((parseFloat(i.unit_cost) || 0) * 1.10 * (parseFloat(i.qty) || 1) * 100) / 100, 0);
                    return (
                      <>
                        <div className="flex justify-between text-xs">
                          <span className="text-[#5C6A7A] font-['IBM_Plex_Sans']">Subtotal (est. 10% markup)</span>
                          <span className="font-['JetBrains_Mono'] text-[#B8C0CC]">{fmt(subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-[#5C6A7A] font-['IBM_Plex_Sans']">Service Fee (est.)</span>
                          <span className="font-['JetBrains_Mono'] text-[#B8C0CC]">{fmt(75)}</span>
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t border-[#2D3F5E]">
                          <span className="text-white font-['Manrope'] font-bold">Estimated Total</span>
                          <span className="font-['JetBrains_Mono'] text-[#3B82F6] font-bold text-base">{fmt(subtotal + 75)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {items.length > 0 && (
            <div className="flex justify-end">
              <button
                onClick={() => navigate(`/ops/invoice-transformer/build/${id}`)}
                className="flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors shadow-[0_0_20px_rgba(59,130,246,0.25)]"
                data-testid="build-invoice-btn">
                Build Customer Invoice <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Manual Search Modal */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent className="max-w-xl bg-[#1A2332] border-[#2D3F5E] text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-['Manrope']">
              Search Vendor Catalog
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans']">
              Searching for: <strong className="text-[#B8C0CC]">{items[matchItemIdx]?.desc}</strong>
              {items[matchItemIdx] && <span className="ml-2">@ {fmt(items[matchItemIdx]?.unit_cost)}</span>}
            </p>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A7A]" />
              <input
                type="text" value={searchQuery} onChange={e => handleSearch(e.target.value)}
                placeholder="Search parts, descriptions..."
                className="w-full h-10 pl-9 pr-4 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]"
                data-testid="catalog-search-input"
                autoFocus
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {searching && <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-[#3B82F6]" /></div>}
              {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                <p className="text-center py-6 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No catalog items found</p>
              )}
              {searchResults.map(item => {
                const invoicePrice = parseFloat(items[matchItemIdx]?.unit_cost) || 0;
                const diff = Math.round((invoicePrice - item.unit_price) * 100) / 100;
                return (
                  <button key={item.id} onClick={() => applyManualMatch(item)}
                    className="w-full text-left p-3 rounded-sm bg-[#0A0F1A] border border-[#2D3F5E] hover:border-[#3B82F6]/50 transition-colors"
                    data-testid={`catalog-result-${item.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="text-sm font-['IBM_Plex_Sans'] text-white truncate">{item.description}</p>
                        <p className="text-[10px] text-[#5C6A7A] font-['JetBrains_Mono']">
                          {item.vendor_name} · {item.category}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-['JetBrains_Mono'] font-bold text-[#3B82F6]">{fmt(item.unit_price)}</p>
                        {diff !== 0 && (
                          <p className={`text-[10px] font-['JetBrains_Mono'] ${diff > 0 ? 'text-red-400' : 'text-[#57EB31]'}`}>
                            {diff > 0 ? '+' : ''}{fmt(diff)}
                          </p>
                        )}
                        {diff === 0 && <p className="text-[10px] font-['JetBrains_Mono'] text-[#57EB31]">Exact match</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
